/**
 * Minimal, dependency-free ZIP builder (STORE method — no compression) for
 * bundling a handful of brand assets in the browser at click-time. We don't
 * commit a zip; we assemble it on demand from the already-served public assets
 * plus generated text, so it never drifts from the page.
 *
 * STORE (uncompressed) keeps this tiny and correct: a few PNG/SVG/MD files in
 * one archive is fine without DEFLATE, and it avoids pulling in a zip library.
 */

export interface ZipEntry {
  name: string;
  data: Uint8Array;
}

// CRC-32 (IEEE 802.3), table built once.
const CRC_TABLE = (() => {
  const t = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    t[n] = c >>> 0;
  }
  return t;
})();

function crc32(bytes: Uint8Array): number {
  let c = 0xffffffff;
  for (let i = 0; i < bytes.length; i++) {
    c = CRC_TABLE[(c ^ bytes[i]!)! & 0xff]! ^ (c >>> 8);
  }
  return (c ^ 0xffffffff) >>> 0;
}

/** Build a STORE-method .zip from the given entries. */
export function makeZip(entries: ZipEntry[]): Blob {
  const enc = new TextEncoder();
  const chunks: Uint8Array[] = [];
  const central: Uint8Array[] = [];
  let offset = 0;

  const u16 = (n: number): number[] => [n & 0xff, (n >>> 8) & 0xff];
  const u32 = (n: number): number[] => [
    n & 0xff,
    (n >>> 8) & 0xff,
    (n >>> 16) & 0xff,
    (n >>> 24) & 0xff,
  ];

  for (const entry of entries) {
    const nameBytes = enc.encode(entry.name);
    const crc = crc32(entry.data);
    const size = entry.data.length;

    // Local file header.
    const local = new Uint8Array([
      ...u32(0x04034b50), // signature
      ...u16(20), // version needed
      ...u16(0), // flags
      ...u16(0), // method: store
      ...u16(0), // mod time
      ...u16(0), // mod date
      ...u32(crc),
      ...u32(size), // compressed size
      ...u32(size), // uncompressed size
      ...u16(nameBytes.length),
      ...u16(0), // extra length
      ...nameBytes,
    ]);
    chunks.push(local, entry.data);

    // Central directory record.
    central.push(
      new Uint8Array([
        ...u32(0x02014b50),
        ...u16(20), // version made by
        ...u16(20), // version needed
        ...u16(0), // flags
        ...u16(0), // method
        ...u16(0), // time
        ...u16(0), // date
        ...u32(crc),
        ...u32(size),
        ...u32(size),
        ...u16(nameBytes.length),
        ...u16(0), // extra
        ...u16(0), // comment
        ...u16(0), // disk start
        ...u16(0), // internal attrs
        ...u32(0), // external attrs
        ...u32(offset), // local header offset
        ...nameBytes,
      ]),
    );

    offset += local.length + entry.data.length;
  }

  const centralSize = central.reduce((sum, c) => sum + c.length, 0);
  const end = new Uint8Array([
    ...u32(0x06054b50),
    ...u16(0), // disk
    ...u16(0), // central dir disk
    ...u16(entries.length),
    ...u16(entries.length),
    ...u32(centralSize),
    ...u32(offset), // central dir offset
    ...u16(0), // comment length
  ]);

  // Concatenate into one ArrayBuffer-backed view so the Blob types line up
  // regardless of the lib's Uint8Array<ArrayBufferLike> widening.
  const parts = [...chunks, ...central, end];
  const total = parts.reduce((sum, p) => sum + p.length, 0);
  const out = new Uint8Array(total);
  let pos = 0;
  for (const p of parts) {
    out.set(p, pos);
    pos += p.length;
  }
  return new Blob([out.buffer], { type: "application/zip" });
}

/** Trigger a browser download of `blob` as `filename`. */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
